import Typography from '@mui/material/Typography';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useForm } from 'react-final-form';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import useWizardContext from '@swf/core/hooks/useWizardContext';

import type { DepositPayload, DepositResult } from '../../../types/Cashier';
import {
  convertAmountByRate,
  getCurrencyRate,
  isZeroExponentCurrency
} from '../../../utils/cashier';
import Prompt from '../../Prompt/Prompt';

import {
  TXTWCL_Do_you_still_want_to_continueX,
  TXTWCL_New_Deposit_Amount,
  TXTWCL_NoX_change_deposit,
  TXTWCL_This_is_because_the_current_ex_ELL,
  TXTWCL_YesX_deposit_N0,
  TXTWCL_Your_deposit_amount_has_change_ELL_1
} from './LessMoney.messages.json';

interface Props {
  deposit: DepositPayload;
  depositResult: DepositResult;
  onClose(): void;
}

const LessMoney: React.FC<Props> = ({
  depositResult: {
    paySystemDetails: {
      rates: { rates: updatedRates }
    }
  },
  deposit: { amount, balanceCurrency, currency, rate: initialRate },
  onClose
}) => {
  const { data } = useWizardContext();
  const form = useForm();
  const isBalanceCurrencyZeroExponent = isZeroExponentCurrency(balanceCurrency);

  const closePrompt = () => {
    onClose();
    data.depositResult = null;
  };

  const submitForm = () => form.submit();

  const newAmount = (
    <CurrencyAmount
      value={convertAmountByRate(
        Number(amount),
        getCurrencyRate(updatedRates, currency),
        balanceCurrency,
        false
      )}
      isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
      currency={balanceCurrency}
      currencyDisplay="code"
      dollarValue
    />
  );

  const sourceCurrencyAmount = (
    <CurrencyAmount currency={currency} currencyDisplay="code" dollarValue value={1} />
  );

  const targetCurrencyAmount = (value: number) => (
    <CurrencyAmount
      value={convertAmountByRate(1, value, balanceCurrency, false, 4)}
      isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
      currency={balanceCurrency}
      currencyDisplay="code"
      minimumFractionDigits={4}
      maximumFractionDigits={4}
      dollarValue
    />
  );

  return (
    <Prompt
      variant="info"
      onClose={closePrompt}
      onPrimaryButtonClick={submitForm}
      title={{
        translation: TXTWCL_New_Deposit_Amount,
        values: {
          0: newAmount
        }
      }}
      primaryButton={{
        translation: TXTWCL_YesX_deposit_N0,
        values: {
          0: newAmount
        }
      }}
      secondaryButton={{
        translation: TXTWCL_NoX_change_deposit
      }}
      alert={
        <div data-testid="alert-text">
          <Translation
            {...TXTWCL_Your_deposit_amount_has_change_ELL_1}
            values={{
              0: newAmount
            }}
          />{' '}
          <Translation
            {...TXTWCL_This_is_because_the_current_ex_ELL}
            values={{
              0: (
                <>
                  {sourceCurrencyAmount}
                  {' = '}
                  {targetCurrencyAmount(initialRate)}
                </>
              ),
              1: (
                <>
                  {sourceCurrencyAmount}
                  {' = '}
                  {targetCurrencyAmount(getCurrencyRate(updatedRates, currency))}
                </>
              )
            }}
          />
        </div>
      }
    >
      <Typography>
        <Translation {...TXTWCL_Do_you_still_want_to_continueX} />
      </Typography>
    </Prompt>
  );
};
export default LessMoney;
